import React, { useEffect, useState, useCallback, useMemo, lazy, Suspense } from "react";
import { AiFillDelete } from "react-icons/ai";
import delete_Vector from "../../utils/delete_Vector.jpg";
import LeadStageModal from "../../modals/LeadStageModal";
import expand from "../../utils/expand.png";
import { useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "../../modals/BreadcrumbsContext";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";

// ✅ PERF FIX 1: Lazy-load Drawer — not part of initial bundle, loaded only when first opened
const Drawer = lazy(() => import("./Drawer"));

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50; // rows per page per stage group

const stageOrder = ["new", "discovery", "proposal", "negotiation", "won", "lost"];

// ─── Helpers (defined outside component so they are never re-created) ─────────
const splitText = (str) => str.split(",");

const arrangeSalesPersonNameArray = (arr) => {
  const mapped = arr.map((t) => {
    if (t.includes("@"))            return "Email : " + t;
    if (!isNaN(t.replace(/\s/g, ""))) return "Mob : " + t;
    return t;
  });
  return [
    mapped.filter((i) => !i.includes("@") && !i.includes("Mob")),
    mapped.filter((i) => i.includes("Mob")),
    mapped.filter((i) => i.includes("@")),
  ];
};

// ─── Memoised row ─────────────────────────────────────────────────────────────
// ✅ PERF FIX 2: React.memo — only the row whose checkbox changed re-renders,
//    not the entire stage group
const LeadRow = React.memo(({
  row, isSelected, onCheck, onView, calculateAgedDays, stageMapping,
}) => {
  const salesDisplay = row.lead_salespersonName
    ? arrangeSalesPersonNameArray(splitText(row.lead_salespersonName))?.[0]?.[0] ?? "--"
    : "--";

  return (
    <tr className={isSelected ? "bg-blue-100" : ""}>
      {/* checkbox */}
      <td className="shadow-sm px-1 py-0.5 w-[20px] text-left">
        <div className="flex justify-between items-center w-auto">
          <input
            type="checkbox"
            className="rounded border border-sm"
            checked={isSelected}
            onChange={() => onCheck(row.lead_id)}
          />
          {row?.Cnt > 0 && (
            <div className="w-1 h-1 bg-green-500 text-xs p-1 rounded-lg" />
          )}
        </div>
      </td>

      {/* lead id */}
      <td className="shadow-sm px-1 py-0.5 w-[100px]">
        <div>{row.lead_id}</div>
      </td>

      {/* date */}
      <td className="shadow-sm px-1 py-0.5 w-[200px]" style={{ whiteSpace: "nowrap" }}>
        <div>{format(row.lead_entryTime, "dd-MM-yyyy")}</div>
      </td>

      {/* salesperson */}
      <td className="shadow-sm px-1 py-0.5 w-[300px]">
        <div>{salesDisplay}</div>
      </td>

      {/* organisation */}
      <td className="shadow-sm px-1 py-0.5 w-[380px]">
        <div className="flex items-start justify-between gap-2">
          {row.lead_organization}
          {row.lead_teamid !== "0" && (
            <div style={{
              backgroundColor: "#000000", borderRadius: "17px",
              padding: "2px 10px", color: "#ffffff", fontSize: "12px",
            }}>
              {row.lead_teamid}
            </div>
          )}
          <div className="flex items-center justify-center gap-2">
            <img
              onClick={() => onView(row.lead_id)}
              src={expand}
              alt="expand-icon"
              className="w-4 h-4 cursor-pointer"
            />
            <div className="text-xs font-thin bg-gray-400 px-0.5 rounded-lg text-white">
              {calculateAgedDays(row.lead_entryTime)}
            </div>
          </div>
        </div>
      </td>

      {/* stage badge */}
      <td className="shadow-sm px-1 py-0.5 w-[100px] cursor-pointer">
        {row.lead_stage && (
          <button className={`w-full text-white text-center p-1 rounded ${
            row.lead_stage === "new"         ? "bg-yellow-500" :
            row.lead_stage === "discovery"   ? "bg-teal-500"   :
            row.lead_stage === "proposal"    ? "bg-sky-500"    :
            row.lead_stage === "negotiation" ? "bg-orange-600" :
            row.lead_stage === "won"         ? "bg-green-600"  :
            row.lead_stage === "lost"        ? "bg-red-600"    : "bg-blue-500"
          }`}>
            {stageMapping[row.lead_stage] || row.lead_stage}
          </button>
        )}
      </td>

      <td><div>{row.lead_CampaignId}</div></td>
      <td className="shadow-sm px-1 py-0.5 w-[100px]"><div>{row.lead_primarycontact}</div></td>
      <td className="shadow-sm px-1 py-0.5 w-[100px]">{row.lead_primarycontacttitle}</td>
      <td className="shadow-sm px-1 py-0.5 w-[150px]">{row.lead_email}</td>
      <td className="shadow-sm px-1 py-0.5 w-[100px] text-center">{row.lead_phone}</td>
      <td className="shadow-sm px-1 py-0.5 w-[150px]">
        {row.lead_assignedto?.length > 0
          ? <a href={`http://maps.google.com/?q=${row.lead_assignedto}`}>View</a>
          : ""}
      </td>
      <td className="shadow-sm px-1 py-0.5 w-[50px] text-center">{row.lead_probability}</td>
    </tr>
  );
});

// ─── Per-stage pagination controls ───────────────────────────────────────────
const StagePagination = React.memo(({ stage, currentPage, totalPages, totalRecords, onPageChange }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) range.push(i);

    if (range[0] > 1) {
      if (range[0] > 2) range.unshift("...");
      range.unshift(1);
    }
    if (range[range.length - 1] < totalPages) {
      if (range[range.length - 1] < totalPages - 1) range.push("...");
      range.push(totalPages);
    }
    return range;
  };

  const start = (currentPage - 1) * PAGE_SIZE + 1;
  const end   = Math.min(currentPage * PAGE_SIZE, totalRecords);

  return (
    <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-200 bg-white">
      <span className="text-xs text-gray-500">
        Showing <span className="font-medium">{start}</span>–
        <span className="font-medium">{end}</span> of{" "}
        <span className="font-medium">{totalRecords}</span>
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(stage, currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2 py-0.5 text-xs rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
        >‹</button>

        {getPageNumbers().map((p, idx) =>
          p === "..." ? (
            <span key={`el-${idx}`} className="px-1 text-xs text-gray-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(stage, p)}
              className={`px-2 py-0.5 text-xs rounded border ${
                p === currentPage
                  ? "bg-blue-500 text-white border-blue-500"
                  : "border-gray-200 hover:bg-gray-100"
              }`}
            >{p}</button>
          )
        )}

        <button
          onClick={() => onPageChange(stage, currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2 py-0.5 text-xs rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
        >›</button>
      </div>
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────
const QualifiedGrouped = ({
  stageMapping,
  deleteSelectedLeads,
  calculateAgedDays,
  filteredQualifiedLeadRows,
  selectedLeads,      // array (converted from Set in parent)
  setSelectedLeads,   // bridges back to Set in parent
  rows,
  setRows,
  history,
}) => {

  console.log("inside QualifiedGrouped")
  const [showDrawer, setShowDrawer]       = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLead, setSelectedLead]   = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);

  const [wonLeads, setWonLeads]   = useState(() => JSON.parse(localStorage.getItem("wonLeads"))  || []);
  const [lostLeads, setLostLeads] = useState(() => JSON.parse(localStorage.getItem("lostLeads")) || []);

  // ✅ PERF FIX 3: Per-stage page index stored as { new: 1, discovery: 1, … }
  const [stagePages, setStagePages] = useState({});

  const { showBreadcrumbs, setShowBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const location = useLocation();
  const [quoteLeadId, setQuoteLeadId] = useState("");

  // ─── Group + sort once when data changes ────────────────────────────────────
  // ✅ PERF FIX 4: useMemo replaces useEffect+setState — no extra render cycle
  const groupedQualifiedLeads = useMemo(() => {
    const groups = filteredQualifiedLeadRows?.reduce((acc, lead) => {
      const stage = lead.stage || "new";
      if (!acc[stage]) acc[stage] = [];
      acc[stage].push(lead);
      return acc;
    }, {});

    // Sort groups by stageOrder
    return Object.keys(groups ?? {})
      .sort((a, b) => stageOrder.indexOf(a) - stageOrder.indexOf(b))
      .reduce((sorted, key) => { sorted[key] = groups[key]; return sorted; }, {});
  }, [filteredQualifiedLeadRows]);

  // ✅ PERF FIX 5: selectedLeads as a Set for O(1) lookups inside this component
  const selectedSet = useMemo(() => new Set(selectedLeads), [selectedLeads]);

  // ─── Pagination helpers ──────────────────────────────────────────────────────
  const getPageForStage = useCallback((stage) => stagePages[stage] || 1, [stagePages]);

  const handlePageChange = useCallback((stage, page) => {
    setStagePages((prev) => ({ ...prev, [stage]: page }));
  }, []);

  // Reset pages when data changes (new search/filter)
  useEffect(() => {
    setStagePages({});
  }, [filteredQualifiedLeadRows]);

  // ─── Checkbox handlers ───────────────────────────────────────────────────────
  const handleRowCheck = useCallback((leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  }, [setSelectedLeads]);

  const handleGroupSelectAll = useCallback((e, leads) => {
    const groupIds = leads.map((r) => r.lead_id);
    if (e.target.checked) {
      setSelectedLeads((prev) => Array.from(new Set([...prev, ...groupIds])));
    } else {
      setSelectedLeads((prev) => prev.filter((id) => !groupIds.includes(id)));
    }
  }, [setSelectedLeads]);

  // ─── Drawer ──────────────────────────────────────────────────────────────────
  // ✅ PERF FIX 6: Find the lead once; pass only the single object to Drawer
  const viewLead = useCallback((id) => {
    // Search across all groups
    for (const leads of Object.values(groupedQualifiedLeads)) {
      const lead = leads.find((l) => l.lead_id === id);
      if (lead) {
        setSelectedLead(lead);
        requestAnimationFrame(() => setShowDrawer(true));
        return;
      }
    }
  }, [groupedQualifiedLeads]);

  const handleInputChange = useCallback((e, field) => {
    setSelectedLead((prev) => ({ ...prev, [field]: e.target.value }));
  }, []);

  const handleSave = useCallback((e) => {
    e.preventDefault();
    setShowDrawer(false);
  }, []);

  // ─── Stage modals ────────────────────────────────────────────────────────────
  const openStatusModal  = useCallback((lead) => { setSelectedLead(lead); setShowStageModal(true); }, []);
  const closeStatusModal = useCallback(() => setShowStageModal(false), []);
  const closeDeleteModal = useCallback(() => setShowDeleteModal(false), []);

  const updateStageToWon = useCallback((leadId) => {
    const lead = rows.find((r) => r.lead_id === leadId);
    if (!lead) return;
    const updated = {
      ...lead, stage: "won",
      timeline: Array.isArray(lead.timeline)
        ? [...lead.timeline, { date: new Date().toISOString(), activity: "Stage changed to Won" }]
        : [{ date: new Date().toISOString(), activity: "Stage changed to Won" }],
    };
    setRows((prev) => prev.filter((r) => r.lead_id !== leadId));
    const next = [...wonLeads, updated];
    setWonLeads(next);
    localStorage.setItem("wonLeads", JSON.stringify(next));
    setShowStageModal(false);
  }, [rows, wonLeads, setRows]);

  const updateStageToLost = useCallback((leadId) => {
    const lead = rows.find((r) => r.lead_id === leadId);
    if (!lead) return;
    const updated = { ...lead, stage: "lost" };
    setRows((prev) => prev.filter((r) => r.lead_id !== leadId));
    const next = [...lostLeads, updated];
    setLostLeads(next);
    localStorage.setItem("lostLeads", JSON.stringify(next));
    setShowStageModal(false);
  }, [rows, lostLeads, setRows]);

  const updateLeadStage = useCallback((leadId, newStage) => {
    setRows((prev) => prev.map((r) => r.lead_id === leadId ? { ...r, stage: newStage } : r));
    closeStatusModal();
  }, [setRows, closeStatusModal]);

  const handleQuoteButtonClick = useCallback((leadId) => {
    setQuoteLeadId(leadId);
    navigate(`/qualifiedLeadsTable/quoteLead/${leadId}`);
    setShowDrawer(false);
    setShowBreadcrumbs(true);
  }, [navigate, setShowBreadcrumbs]);

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="overflow-x-auto">

      {/* Bulk delete button */}
      {selectedLeads?.length > 0 && (
        <div className="mb-2">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-500 text-white px-2 py-2 rounded mr-2"
            disabled={selectedLeads.length === 0}
          >
            <AiFillDelete size={18} />
          </button>
        </div>
      )}

      {/* ── One table per stage group ── */}
      {Object.entries(groupedQualifiedLeads).map(([stage, leads]) => {
        const currentPage = getPageForStage(stage);
        const totalPages  = Math.ceil(leads.length / PAGE_SIZE);

        // ✅ Only slice the current page — rest stays in memory, not in the DOM
        const pagedLeads  = leads.slice(
          (currentPage - 1) * PAGE_SIZE,
          currentPage * PAGE_SIZE
        );

        const allGroupSelected = leads.every((r) => selectedSet.has(r.lead_id));

        return (
          <div key={stage} className="mb-6">
            <h2 className="text-lg font-thin mb-2">
              {stageMapping[stage] || stage} Leads ({leads.length})
            </h2>

            <table className="table-auto w-full border-collapse border border-gray-200">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="shadow-sm px-1 py-0.5 w-[20px] text-left">
                    <input
                      type="checkbox"
                      className="rounded border border-sm shadow"
                      onChange={(e) => handleGroupSelectAll(e, leads)}
                      checked={selectedLeads.length > 0 && allGroupSelected}
                    />
                  </th>
                  <th className="shadow-sm px-1 py-0.5 text-left font-normal w-[100px]">Lead ID</th>
                  <th className="shadow-sm px-1 py-0.5 text-left font-normal w-[200px]" style={{ whiteSpace: "nowrap" }}>Date</th>
                  <th className="shadow-sm px-1 py-0.5 text-left font-normal w-[300px]">SalesPerson</th>
                  <th className="shadow-sm px-1 py-0.5 w-[380px] text-left font-normal">Customer / Organization</th>
                  <th className="shadow-sm px-1 py-0.5 w-[100px] text-left font-normal">Stage</th>
                  <th className="shadow-sm px-1 py-0.5 w-[100px] text-left font-normal">Project</th>
                  <th className="shadow-sm px-1 py-0.5 w-[100px] text-left font-normal">Contact Person</th>
                  <th className="shadow-sm px-1 py-0.5 w-[100px] text-left font-normal">Title</th>
                  <th className="shadow-sm px-1 py-0.5 w-[150px] text-left font-normal">Email</th>
                  <th className="shadow-sm px-1 py-0.5 w-[100px] text-left font-normal">Phone</th>
                  <th className="shadow-sm px-1 py-0.5 w-[150px] text-left font-normal">Entry Location</th>
                  <th className="shadow-sm px-1 py-0.5 w-[50px] text-left font-normal">Probability</th>
                </tr>
              </thead>
              <tbody className="bg-white px-2">
                {/* ✅ pagedLeads: only PAGE_SIZE rows in the DOM per group */}
                {pagedLeads.map((row) => (
                  <LeadRow
                    key={row.lead_id}
                    row={row}
                    isSelected={selectedSet.has(row.lead_id)}
                    onCheck={handleRowCheck}
                    onView={viewLead}
                    calculateAgedDays={calculateAgedDays}
                    stageMapping={stageMapping}
                  />
                ))}
              </tbody>
            </table>

            {/* ✅ Per-stage pagination bar */}
            <StagePagination
              stage={stage}
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={leads.length}
              onPageChange={handlePageChange}
            />
          </div>
        );
      })}

      {/* Delete modal */}
      {showDeleteModal && selectedLeads.length > 0 && (
        <div className="fixed inset-0 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center relative">
            <div className="flex justify-center mb-2">
              <img src={delete_Vector} alt="Delete Icon" className="w-1/2 h-auto max-w-xs" />
            </div>
            <button className="absolute top-6 right-8 text-gray-500 hover:text-black" onClick={closeDeleteModal}>✖</button>
            <div className="text-xl font-semibold text-black">
              {selectedLeads.length === 1 ? "Lead Selected" : "Leads Selected"}
            </div>
            <div className="text-gray-700 mb-6">
              {selectedLeads.length === 1 ? (
                <span className="font-bold">Are you sure you want to delete?</span>
              ) : (
                <span className="font-bold">Are you sure you want to delete {selectedLeads.length} leads?</span>
              )}
            </div>
            <div className="flex justify-center space-x-4">
              <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600" onClick={deleteSelectedLeads}>Delete</button>
              <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300" onClick={closeDeleteModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Stage modal */}
      {showStageModal && selectedLead && (
        <LeadStageModal
          closeStatusModal={closeStatusModal}
          updateStageToWon={updateStageToWon}
          updateStageToLost={updateStageToLost}
          selectedLead={selectedLead}
          updateLeadStage={updateLeadStage}
        />
      )}

      {/* ✅ Drawer: lazy-loaded, only mounted when open, receives single lead */}
      {showDrawer && selectedLead && (
        <Suspense fallback={null}>
          <Drawer
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            selectedLead={selectedLead}
            handleInputChange={handleInputChange}
            handleSave={handleSave}
            rows={rows}
            setRows={setRows}
            leadsWithHistory={history}
            stageMapping={stageMapping}
            handleQuoteButtonClick={handleQuoteButtonClick}
            setShowBreadcrumbs={setShowBreadcrumbs}
          />
        </Suspense>
      )}
    </div>
  );
};

export default QualifiedGrouped;